import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import tw from "twin.macro";
import styled from "styled-components";
import { ReactComponent as CalendarIcon } from "feather-icons/dist/icons/calendar.svg";
import { ReactComponent as ClockIcon } from "feather-icons/dist/icons/clock.svg";
import { ReactComponent as RefreshIcon } from "feather-icons/dist/icons/refresh-cw.svg";
import { ReactComponent as TagIcon } from "feather-icons/dist/icons/tag.svg";

import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts.js";

import { getPostBySlug, getRelatedPosts, getAdjacentPosts } from "../data/posts";
import { formatDate, readingTimeLabel } from "../utils/text";
import usePageMeta from "../utils/usePageMeta";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "../config";
import { CategoryBadge, MetaRow, MetaItem, TagChip, PrimaryButtonLink } from "../components/ui";
import CoverArt from "../components/CoverArt";
import Avatar from "../components/Avatar";
import Breadcrumb from "../components/Breadcrumb";
import ArticleBody, { extractHeadings } from "../components/ArticleBody";
import ReferencesList from "../components/ReferencesList";
import TableOfContents from "../components/TableOfContents";
import ShareBar from "../components/ShareBar";
import AuthorCard from "../components/AuthorCard";
import RelatedPosts from "../components/RelatedPosts";
import PrevNext from "../components/PrevNext";
import BlogCta from "../components/BlogCta";

/** Page article : /blog/:slug */

const ProgressBar = styled.div`
  ${tw`fixed top-0 left-0 h-1 bg-primary-500 z-50 pointer-events-none`}
  width: ${p => p.$progress}%;
  transition: width 120ms linear;
`;

// Moins d'air au-dessus de l'article que sur les pages de sections (le fil d'Ariane suffit à respirer).
const Content = tw(ContentWithPaddingXl)`pt-8 lg:pt-12`;
const Header = tw.header`max-w-3xl mx-auto text-center`;
const TopRow = tw.div`flex flex-col sm:flex-row items-center justify-center`;
const Meta = tw(MetaRow)`mt-3 sm:mt-0 sm:ml-6 justify-center`;
const Title = tw.h1`mt-6 text-3xl sm:text-4xl lg:text-5xl font-black tracking-wide leading-tight text-gray-900`;
const Excerpt = tw.p`mt-6 text-lg sm:text-xl text-gray-700 font-medium leading-relaxed`;
const AuthorLine = tw.div`mt-8 flex items-center justify-center`;
const Portrait = tw(Avatar)`mr-3`;
const AuthorText = tw.div`text-left`;
const AuthorName = tw.p`text-sm font-bold text-gray-900`;
const AuthorMeta = tw.p`text-xs font-medium text-gray-600`;
const UpdatedIcon = tw(RefreshIcon)`inline w-3 h-3`;

const Cover = tw.div`mt-12 rounded-lg overflow-hidden shadow-raised h-64 sm:h-80 lg:h-128 relative`;
const CoverCredit = tw.p`mt-3 text-xs text-gray-500 text-right font-medium`;

const Layout = tw.div`mt-12 lg:mt-16 flex flex-col lg:flex-row`;
const Main = tw.article`w-full lg:w-2/3 lg:pr-16 max-w-3xl mx-auto lg:mx-0`;
const Sidebar = tw.aside`hidden lg:block lg:w-1/3`;
const Sticky = styled.div`
  position: sticky;
  top: 2rem;
`;
const SidebarBlock = tw.div`mt-8 first:mt-0`;

const TagsRow = styled.div`
  ${tw`mt-12 flex flex-wrap items-center`}
  svg {
    ${tw`w-4 h-4 text-gray-500 mr-2 mb-2`}
  }
`;
const Tag = tw(TagChip)`mr-2 mb-2`;
const MobileShare = tw.div`mt-8 lg:hidden`;
const AuthorWrap = tw.div`mt-12`;
const Breadcrumbs = tw.div`mb-8`;

const NotFoundWrap = tw.div`text-center max-w-xl mx-auto py-16`;
const NotFoundTitle = tw.h1`text-3xl sm:text-4xl font-black tracking-wide text-gray-900`;
const NotFoundText = tw.p`mt-4 text-gray-600 font-medium`;
const NotFoundAction = tw(PrimaryButtonLink)`mt-8`;

const useReadingProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const update = () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, Math.max(0, (window.scrollY / total) * 100)) : 0);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);
  return progress;
};

/** Identifiants des références réellement citées dans le texte ([^n]). */
const getCitedIds = content => {
  const ids = new Set();
  (content || []).forEach(b => {
    const text = [b.title, b.text, ...(b.items || []), ...((b.facts || []).map(f => `${f.label} ${f.value}`))].filter(Boolean).join(" ");
    (text.match(/\[\^(\d+)\]/g) || []).forEach(m => ids.add(m.replace(/\D/g, "")));
  });
  return ids;
};

function NotFound() {
  usePageMeta({ title: "Article introuvable", description: "Cet article n'existe pas ou a été déplacé.", path: "/blog", noindex: true });
  return (
    <AnimationRevealPage disabled>
      <Container>
        <ContentWithPaddingXl>
          <NotFoundWrap>
            <NotFoundTitle>Article introuvable</NotFoundTitle>
            <NotFoundText>Cet article n'existe pas ou a été déplacé. Retrouvez tous nos articles sur le blog.</NotFoundText>
            <NotFoundAction to="/blog">Retour au blog</NotFoundAction>
          </NotFoundWrap>
        </ContentWithPaddingXl>
      </Container>
    </AnimationRevealPage>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  if (!post) return <NotFound />;
  return <Article post={post} />;
}

function Article({ post }) {
  const progress = useReadingProgress();
  const headings = useMemo(() => extractHeadings(post.content), [post]);
  const citedIds = useMemo(() => getCitedIds(post.content), [post]);
  const related = useMemo(() => getRelatedPosts(post, 3), [post]);
  const { newer, older } = useMemo(() => getAdjacentPosts(post), [post]);
  const url = `${SITE_URL}/blog/${post.slug}`;
  const hasLead = post.content.length > 0 && post.content[0].type === "lead";
  // Image de partage (Open Graph / Twitter) : les couvertures sont servies depuis le site,
  // il faut donc les rendre absolues — sans quoi tous les articles partagent le logo du site.
  const image = post.cover.image
    ? /^https?:\/\//i.test(post.cover.image)
      ? post.cover.image
      : `${SITE_URL}${post.cover.image}`
    : DEFAULT_OG_IMAGE;

  usePageMeta({
    // Titre/description dédiés au référencement (voir `seoTitle` / `seoDescription` dans les
    // fichiers d'articles) : plus courts que le titre éditorial, qui reste celui du <h1>.
    title: post.seoTitle || post.title,
    description: post.seoDescription || post.excerpt,
    path: `/blog/${post.slug}`,
    type: "article",
    image,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.seoDescription || post.excerpt,
        image,
        datePublished: post.publishedAt,
        dateModified: post.updatedAt,
        author: { "@type": "Person", name: post.author.name, jobTitle: post.author.role },
        publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL, logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE } },
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        articleSection: post.category.label,
        keywords: post.tags.join(", "),
        inLanguage: "fr-FR",
        wordCount: undefined,
        citation: post.references.filter(r => r.url).map(r => r.url),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
          { "@type": "ListItem", position: 3, name: post.category.label, item: `${SITE_URL}/blog/categorie/${post.category.slug}` },
          { "@type": "ListItem", position: 4, name: post.title, item: url },
        ],
      },
      // Données structurées supplémentaires propres à l'article (Person, Organization, Event…),
      // déclarées dans le champ `jsonLd` du fichier de l'article. Voir src/blog/README.md.
      ...(Array.isArray(post.jsonLd) ? post.jsonLd : post.jsonLd ? [post.jsonLd] : []),
    ],
  });

  // Un Set neuf à chaque rendu : ArticleBody y note la première occurrence de chaque citation (id="cite-n").
  const seenCitations = new Set();

  return (
    <AnimationRevealPage disabled>
      <ProgressBar $progress={progress} aria-hidden="true" />
      <Container>
        <Content>
          <Breadcrumbs>
            <Breadcrumb
              items={[
                { label: "Accueil", to: "/" },
                { label: "Blog", to: "/blog" },
                { label: post.category.label, to: `/blog/categorie/${post.category.slug}` },
                { label: post.title },
              ]}
            />
          </Breadcrumbs>

          <Header>
            <TopRow>
              <CategoryBadge category={post.category} />
              <Meta>
                <MetaItem>
                  <CalendarIcon aria-hidden="true" />
                  <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
                </MetaItem>
                <MetaItem>
                  <ClockIcon aria-hidden="true" />
                  {readingTimeLabel(post.readingTime)}
                </MetaItem>
              </Meta>
            </TopRow>
            <Title>{post.title}</Title>
            {!hasLead && post.excerpt && <Excerpt>{post.excerpt}</Excerpt>}
            <AuthorLine>
              <Portrait name={post.author.name} image={post.author.image} size={48} />
              <AuthorText>
                <AuthorName>{post.author.name}</AuthorName>
                <AuthorMeta>
                  {post.author.role}
                  {post.updatedAt && post.updatedAt !== post.publishedAt && (
                    <>
                      {" · "}
                      <UpdatedIcon aria-hidden="true" /> Mis à jour le <time dateTime={post.updatedAt}>{formatDate(post.updatedAt)}</time>
                    </>
                  )}
                </AuthorMeta>
              </AuthorText>
            </AuthorLine>
          </Header>

          <Cover>
            <CoverArt seed={post.slug} color={post.category.color} image={post.cover.image} plain={post.cover.plain} alt={post.cover.alt || ""} eager />
          </Cover>
          {post.cover.credit && <CoverCredit>{post.cover.credit}</CoverCredit>}

          <Layout>
            <Main>
              <ArticleBody content={post.content} seenCitations={seenCitations} />

              {post.tags.length > 0 && (
                <TagsRow>
                  <TagIcon aria-hidden="true" />
                  {post.tags.map((t, i) => (
                    <Tag key={post.tagSlugs[i]} to={`/blog/tag/${post.tagSlugs[i]}`}>
                      {t}
                    </Tag>
                  ))}
                </TagsRow>
              )}

              <MobileShare>
                <ShareBar url={url} title={post.title} text={post.excerpt} />
              </MobileShare>

              <ReferencesList references={post.references} citedIds={citedIds} />

              <AuthorWrap>
                <AuthorCard author={post.author} />
              </AuthorWrap>

              <PrevNext older={older} newer={newer} />
            </Main>

            <Sidebar>
              <Sticky>
                <SidebarBlock>
                  <TableOfContents headings={headings} />
                </SidebarBlock>
                <SidebarBlock>
                  <ShareBar url={url} title={post.title} text={post.excerpt} />
                </SidebarBlock>
              </Sticky>
            </Sidebar>
          </Layout>

          <RelatedPosts posts={related} />
        </Content>
      </Container>
      <BlogCta />
    </AnimationRevealPage>
  );
}
